import { FeatureCollection } from 'geojson';
import mapboxgl, { LngLatBounds } from 'mapbox-gl';
import { addAchievementMarkersFromCollection } from '../../js/map/achievement_markers';
import GeojsonMapController from '../../js/map/geojson_map_controller';
import { journeyPathLayers } from '../../js/map/journey_path_layers';
import type { MapLayerSpecification } from '../../js/map/map_layer_specification';

type MapImageCoordinates = [
  [number, number],
  [number, number],
  [number, number],
  [number, number]
];

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN!;

export default class NonGeoMapController extends GeojsonMapController {
  declare private readonly mapImageUrlValue: string;
  declare private readonly mapImageBoundsValue: MapImageCoordinates;

  public static readonly values = {
    ...GeojsonMapController.values,
    config: Object,
    geoJsonUrl: String,
    mapImageUrl: String,
    mapImageBounds: Array
  };

  protected _mapReadyEvent(): 'load' | 'style.load' {
    return 'load';
  }

  protected _mapOptions(): Partial<mapboxgl.MapboxOptions> {
    return {
      pitch: 0,
      clickTolerance: 10,
      maxPitch: 0,
      zoom: 5,
      style: { version: 8, sources: {}, layers: [] }
    };
  }

  protected _beforeSetupMap(): void {
    this.map.addSource('baseImage', {
      type: 'image',
      url: this.mapImageUrlValue,
      coordinates: this.mapImageBoundsValue
    });

    this.map.addLayer({
      id: 'baseImage-layer',
      type: 'raster',
      source: 'baseImage',
      paint: {
        'raster-fade-duration': 0
      }
    });

    const bounds = new LngLatBounds();

    this.mapImageBoundsValue.forEach(coord => bounds.extend(coord));
    this.map.setMaxBounds(bounds);
    this.map.setCenter(bounds.getCenter());
  }

  protected _lineLayers(): MapLayerSpecification[] {
    const journeyLayers: MapLayerSpecification[] = journeyPathLayers({
      lineWidth: 5,
      remainingOpacity: 0.5
    });

    const previewPathLayer: MapLayerSpecification = {
      id: 'preview-path',
      source: 'map-data',
      type: 'line',
      filter: ['==', ['get', 'variant'], 'preview-path'],
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'blue',
        'line-width': 5
      }
    };

    return journeyLayers.concat(previewPathLayer);
  }

  protected _handlePointFeatures(mapData: FeatureCollection): void {
    addAchievementMarkersFromCollection(
      this.map,
      mapData.features,
      this.markers,
      true
    );
  }
}
